<template>
  <div v-show="hasSelectedWorkpackage">
    <attribute-filter-panel
      :key="'attribute-filter-panel-' + isFilteringByArchitectureGroupEnabled"
      class="attribute-filter-panel"
      :filter-rules="retailAttributesFilter"
      enable-hierarchy-filters
      title-localisation="attributes.filters.filterByAttributes"
      filter-count-localisation="attributes.filters.numApplied"
      @attributeFilterChange="setGridViewFilter"
    />
    <basic-spinner v-if="isLoadingWithoutData" class="spinner-wrapper" />
    <div class="d-flex align-center justify-center flex-column">
      <v-fade-transition>
        <v-row v-if="noProductsMatchingFilters && recalculatingAll" key="recalculating">
          <span class="notification-text">{{
            $t('gridView.noProductsWhileRecalculatingAll')
          }}</span>
        </v-row>
        <v-row v-else-if="noProductsMatchingFilters" key="empty-results">
          <span class="notification-text mb-5">{{ $t('gridView.noResultsToDisplay') }}</span>
        </v-row>
      </v-fade-transition>
    </div>

    <div class="hierarchy__container">
      <table :class="['hierarchy__table', { 'sticky-header': !showAgGridProductList }]">
        <component
          :is="item.component"
          v-for="item in tableRows"
          :key="item.metaId"
          v-bind="item.data"
          @releaseCategory="releaseCategory"
          @releasePricingGroup="releasePG"
          @pricingSpecialistPricingGroupApproval="pricingSpecialistPricingGroupApproval"
          @pricingSpecialistCategoryApproval="pricingSpecialistCategoryApproval"
          @categoryManagerPricingGroupApproval="categoryManagerPricingGroupApproval"
          @categoryManagerCategoryApproval="categoryManagerCategoryApproval"
          @unitApproval="approveUnit"
          @toggleFilteringByArchitectureGroup="onToggleFilteringByArchitectureGroup"
        />
      </table>
    </div>
    <!-- Confirm dialog -->
    <alert-dialog
      :is-open="isReleaseConfirmDialogueOpen"
      :ok-btn-text="$t('actions.ok')"
      @onOk="releasePricingGroup(true)"
      @onCancel="releasePricingGroup(false)"
    >
      <template v-if="categoryToRelease" v-slot:header>
        {{ $t('dialogs.releaseDialogs.headerCategory') }}
      </template>
      <template v-else v-slot:header>
        {{ $t('dialogs.releaseDialogs.headerPricingGroup') }}
      </template>
      <template v-if="categoryToRelease" v-slot:body>
        {{ $t('dialogs.releaseDialogs.bodyCategory') }}
        <strong>{{ categoryToRelease ? categoryToRelease.name : '' }}</strong>
        <v-alert v-if="releaseHasValidationErrors" type="warning" outlined class="mt-3">
          {{ $t('dialogs.releaseDialogs.validationWarning') }}
        </v-alert>
      </template>
      <template v-else v-slot:body>
        {{ $t('dialogs.releaseDialogs.bodyPricingGroup') }}
        <strong>{{ pricingGroupToRelease ? pricingGroupToRelease.name : '' }}</strong>
        <v-alert v-if="releaseHasValidationErrors" type="warning" outlined class="mt-3">
          {{ $t('dialogs.releaseDialogs.validationWarning') }}
        </v-alert>
      </template>
    </alert-dialog>
    <grid-view-add-scenario-dialog />
  </div>
</template>

<script>
import { debounce, fill, findIndex, reduce, slice, get, sortBy, values, isEmpty } from 'lodash';
import { createNamespacedHelpers, mapActions, mapMutations, mapState } from 'vuex';
import to from 'await-to-js';
import {
  getGroupedAggregations,
  getSingleAggregations,
  hideRedundantHierarchyLevels,
  useReleaseCheckboxApprovals,
} from '@enums/feature-flags';
import { rootHierarchyLevel, unitLevel, pricingGroupLevel, categoryLevel } from '@enums/hierarchy';
import { mapAggregations } from '@sharedModules/data/utils/time-flexible-utils';
import sapExportUtilsMixin from '../../../mixins/sapExportUtils';
import clientConfig from '@sharedModules/config/client';

const { mapGetters } = createNamespacedHelpers('gridView');
const { mapState: mapGridViewState } = createNamespacedHelpers('gridView');
const { mapGetters: mapWorkPackageGetters } = createNamespacedHelpers('workpackages');
const { mapGetters: mapHierarchyGetters } = createNamespacedHelpers('hierarchy');
const { mapState: mapContextState } = createNamespacedHelpers('context');
const { mapActions: mapHierarchyActions } = createNamespacedHelpers('hierarchy');
const { mapGetters: mapAttributesGetters } = createNamespacedHelpers('attributes');

export default {
  mixins: [sapExportUtilsMixin],

  props: {
    units: Array,
    isUnitManagerView: Boolean,
  },

  events: {
    onNewCandidateScenarioElected() {
      this.fetchAggregations();
    },

    onToggleExpansion({ parentId, level, beingExpanded }) {
      this.toggleExpansion({
        parentId,
        level,
        beingExpanded,
      });
    },

    onExpandProductView({ levelEntryKey }) {
      if (this.selectedLevelEntryKey === levelEntryKey) this.resetState();
      else {
        this.setSelectedFilter({ filterName: 'selectedLevelEntryKey', filterValue: levelEntryKey });
        if (!this.isUnitManagerView) {
          this.setSelectedFilter({ filterName: 'pricingGroup', filterValue: levelEntryKey });
        }
      }
    },

    async onCompetitorSelectionChanged() {
      if (this.debounceUpdateGridViewInstance) {
        this.debounceUpdateGridViewInstance();
      }
    },

    async onHistoricalPeriodSelectionChanged() {
      if (this.readFromDatabricks) {
        this.updateGridView();
      }
    },
  },

  data() {
    return {
      pricingGroupToRelease: null,
      isReleaseConfirmDialogueOpen: false,
      releaseHasValidationErrors: false,
      debounceUpdateGridViewInstance: null,
    };
  },

  computed: {
    ...mapState('gridView', [
      'hierarchy',
      'rootHierarchyLevelItemId',
      'expandedHierarchyLevelItems',
      'pricingGroupsToRelease',
      'categoryToRelease',
      'selectedHistoricalPeriod',
      'loading',
      'loadingRecalculateAllResults',
      'groupedHierarchy',
      'showAgGridProductList',
    ]),
    ...mapState('workpackages', ['selectedUnitLevelEntryKey', 'selectedWorkpackage']),
    ...mapState('clientConfig', {
      toggleLogic: 'toggleLogic',
      hierarchyConfig: 'hierarchy',
      priceValidationsConfig: 'priceValidations',
    }),
    ...mapContextState(['profile']),
    ...mapGridViewState([
      'isFilteringByArchitectureGroupEnabled',
      'isCachedResult',
      'recalculatingAll',
    ]),
    ...mapGetters([
      'hierarchyLevelItems',
      'hierarchyKey',
      'aggregationsLoading',
      'groupedHierarchyKey',
      'previousGroupedHierarchyKey',
      'readFromDatabricks',
      'isGridViewLoading',
    ]),
    ...mapWorkPackageGetters(['hasSelectedWorkpackage']),
    ...mapHierarchyGetters(['getHierarchy']),
    ...mapAttributesGetters(['toolStoreGroupGlobalAttributeKey']),
    ...mapState('filters', ['selectedLevelEntryKey', 'retailAttributesFilter', 'scenario']),
    ...mapState('storeGroupRelationships', ['storegroupKeyNameMap']),

    noProductsMatchingFilters() {
      return isEmpty(this.tableRows) && !this.isGridViewLoading;
    },
    isLoadingWithoutData() {
      return isEmpty(this.tableRows) && this.isGridViewLoading;
    },

    useReleaseCheckboxApprovals() {
      return this.toggleLogic[useReleaseCheckboxApprovals];
    },

    isPriceValidationsEnabled() {
      return !!(this.priceValidationsConfig && this.priceValidationsConfig.enabled);
    },

    orderedHierarchyLevelItems() {
      // FIXME: come up with nicer way to achieve this
      // slicing here to remove AH entry as we want to render the headers after it
      const sliceEnd = this.isUnitManagerView ? unitLevel + 1 : pricingGroupLevel + 1; // +1 as slice
      return reduce(
        slice(this.expandedHierarchyLevelItems, 0, sliceEnd),
        (orderedItems, parentId, level) => {
          let key;
          let items;
          if (this.toggleLogic[getSingleAggregations]) {
            key = this.hierarchyKey({ level, parentId });
            items = this.hierarchy[key];
          }
          if (this.toggleLogic[getGroupedAggregations] && isEmpty(items)) {
            key = this.groupedHierarchyKey();
            items = get(this.groupedHierarchy, [key, level]);
            if (!items) {
              // get cached data for previous competitors before new ones are loaded on competitor dropdown change
              // so that screen don't jump
              // overlay is shown on top of previous data before new one is loaded
              key = this.previousGroupedHierarchyKey();
              items = get(this.groupedHierarchy, [key, level], {});
            }
            items = values(items).filter(
              // Also filter out the hierarchyItems without PG hierarchies.
              // TODO: the case above shouldn't exist, need to figure out the root cause
              a => a.parentId === parentId && a._id
            );
          }

          const sortedItems = sortBy(items, [item => item.name.toLowerCase()]);

          if (!sortedItems) return orderedItems;
          if (level === this.hierarchyConfig.rootHierarchyLevel) return [...sortedItems];

          const filteredItems = this.isUnitManagerView
            ? sortedItems.filter(({ levelEntryKey }) => this.units.includes(levelEntryKey))
            : sortedItems;
          const parentIx = findIndex(orderedItems, { _id: parentId });

          // if we don't have a parent for the selection ignore.
          if (parentIx === -1) {
            return orderedItems;
          }
          orderedItems.splice(parentIx + 1, 0, ...filteredItems);
          return orderedItems;
        },
        []
      );
    },

    hasOnlyOneUnit() {
      return this.getHierarchy(h => h.level === unitLevel).length === 1;
    },

    hasOnlyOneCategory() {
      return this.getHierarchy(h => h.level === categoryLevel).length === 1;
    },

    hasOnlyOnePricingGroup() {
      return this.getHierarchy(h => h.level === pricingGroupLevel).length === 1;
    },

    tableRows() {
      let fullTableRows = reduce(
        this.orderedHierarchyLevelItems,
        (flatHierarchy, item, ix) => {
          item.toolStoreGroupDescription = this.storegroupKeyNameMap[item.toolStoreGroupKey];
          item._id = get(item, ['_id', 'levelGroupId'], item._id);
          if (ix === 0)
            flatHierarchy.push({
              metaId: 'headers',
              component: 'hierarchy-table-headers',
              data: null,
            });
          const mapAggregationsBySelectedHistoricalPeriod = mapAggregations(
            this.selectedHistoricalPeriod
          );
          const aggregations = this.readFromDatabricks
            ? item
            : mapAggregationsBySelectedHistoricalPeriod(item);
          const staticFields = {
            _id: item._id,
            parentId: item.parentId,
            name: item.name,
            level: item.level,
            levelEntryKey: item.levelEntryKey,
            workpackageId: item.workpackageId,
            released: item.released,
            approvalStatus: item.approvalStatus,
          };
          flatHierarchy.push({
            metaId: item._id,
            component: 'hierarchy-level-item',
            data: {
              isUnitManagerView: this.isUnitManagerView,
              expandedHierarchyLevelItems: this.expandedHierarchyLevelItems,
              ...aggregations,
              ...staticFields,
            },
          });
          // }

          if (item._id === this.selectedLevelEntryKey) {
            flatHierarchy.push({
              metaId: `products::${this.selectedLevelEntryKey}`,
              component: 'hierarchy-product-view',
              data: {
                levelEntryKey: this.selectedLevelEntryKey,
                isUnitManagerView: this.isUnitManagerView,
              },
            });
          }

          return flatHierarchy;
        },
        []
      );
      // remove redundant hierarchy levels
      if (this.toggleLogic[hideRedundantHierarchyLevels]) {
        if (this.hasOnlyOneUnit) {
          fullTableRows = fullTableRows.filter(
            row => get(row, 'data.level', pricingGroupLevel) > rootHierarchyLevel
          );
        }
        if (!this.isUnitManagerView) {
          if (this.hasOnlyOneCategory) {
            fullTableRows = fullTableRows.filter(
              row => get(row, 'data.level', pricingGroupLevel) > unitLevel
            );
          }
          if (this.hasOnlyOnePricingGroup) {
            fullTableRows = fullTableRows.filter(
              row => get(row, 'data.level', pricingGroupLevel) > categoryLevel
            );
          }
        }
        if (get(fullTableRows, '[1].data')) {
          // [0] is headers, [1] is first data row
          fullTableRows[1].data.isFirstTableRow = true; // used for styling when hideRedundantHierarchyLevels
        }
      }
      return fullTableRows;
    },
  },

  async created() {
    this.setLoadingHierarchyView(true);

    this.debounceUpdateGridViewInstance = debounce(
      this.updateGridView,
      clientConfig.inputDebounceValue
    );

    // fetch the aggregations from root -> pg level
    // IMPORTANT: Fetch root ID and set parentId BEFORE fetching grouped aggregations
    // to avoid race condition that causes:
    // 1. First request with empty parentId (gets cached/stale data)
    // 2. Second request with correct parentId (forces refresh)
    await Promise.all([
      this.fetchRootHierarchyLevelItemId(),
      this.fetchAllScenariosForWorkpackage(),
    ]);

    // store the parentId of each hierarchyLevelItem where the array ix is the aggregation level
    await this.setExpandedHierarchyLevelItems({ newExpandedHierarchy: null });

    // Now fetch grouped aggregations with correct parentId set (single request)
    if (this.toggleLogic[getGroupedAggregations]) {
      await this.fetchGroupedAggregatedHierarchyLevelItems({ refreshCache: false });
    }

    const fetchAggregations = [this.fetchHierarchy()];
    if (this.toggleLogic[getSingleAggregations]) {
      fetchAggregations.push(
        ...[
          this.fetchAggregatedHierarchyLevelItems(),
          this.fetchAggregatedHierarchyLevelItems({ aggregationLevel: unitLevel }),
        ]
      );
      // a unit has been clicked on the workpackage screen
      if (this.selectedUnitLevelEntryKey && !this.isUnitManagerView) {
        fetchAggregations.push(
          this.fetchAggregatedHierarchyLevelItems({
            aggregationLevel: categoryLevel,
            parentId: this.selectedUnitLevelEntryKey,
          })
        );
      }
    }

    // don't remove "to", it's required to prevent printing of "unhandled promise rejection" errors
    await to(Promise.all(fetchAggregations));

    if (this.isCachedResult) {
      // fire off another request to get the udpated aggregations
      this.fetchFreshGridviewAggregations();
    }

    // need to expand children in advance because parent rows will not exist to expand them
    if (this.toggleLogic[hideRedundantHierarchyLevels]) {
      if (this.hasOnlyOneUnit) {
        const unit = this.getHierarchy(h => h.level === unitLevel)[0];
        await this.toggleExpansion({
          parentId: unit.levelEntryKey,
          level: unitLevel,
          beingExpanded: true,
          emit: true,
        });
        if (this.isUnitManagerView) {
          this.globalEmit('expand-product-view', { levelEntryKey: unit.levelEntryKey });
        }
      }

      if (!this.isUnitManagerView) {
        if (this.hasOnlyOneCategory) {
          const category = this.getHierarchy(h => h.level === categoryLevel)[0];
          await this.toggleExpansion({
            parentId: category.levelEntryKey,
            level: categoryLevel,
            beingExpanded: true,
            emit: true,
          });
        }
        // // XSR pgs can be too large, causing FE crash due.
        // // avoid auto expand so users can set filters before expanding.
        // if (this.hasOnlyOnePricingGroup) {
        //   const pricingGroup = this.getHierarchy(h => h.level === pricingGroupLevel)[0];
        //   await this.toggleExpansion({
        //     parentId: pricingGroup.levelEntryKey,
        //     level: pricingGroupLevel,
        //     beingExpanded: true,
        //     emit: true,
        //   });
        //   this.globalEmit('expand-product-view', { levelEntryKey: pricingGroup.levelEntryKey });
        // }
      }
    }

    this.getRecalculatingSpecificPricingGroups();
    this.globalEmit('hierarchy-refreshed');
    this.setLoadingHierarchyView(false);
  },

  beforeDestroy() {
    // reset for when changing between unit manager and normal view
    if (this.isUnitManagerView) {
      this.setSelectedFilter({ filterName: 'selectedLevelEntryKey', filterValue: null });
      this.resetExpandedHierarchyLevels();
    }
    if (!this.$route.path.includes('/pricing/')) {
      this.cancelTimeFlexibleAggregationRequests();
    }
    if (this.debounceUpdateGridViewInstance && this.debounceUpdateGridViewInstance.cancel) {
      this.debounceUpdateGridViewInstance.cancel();
    }
  },

  methods: {
    ...mapActions('gridView', [
      'fetchAggregatedHierarchyLevelItems',
      'fetchGroupedAggregatedHierarchyLevelItems',
      'fetchRootHierarchyLevelItemId',
      'fetchItems',
      'setExpandedHierarchyLevelItems',
      'resetPricingGroupsToRelease',
      'setPricingGroupsToRelease',
      'setCategoryToRelease',
      'fetchAggregations',
      'resetExpandedHierarchyLevels',
      'getAlertCounts',
      'toggleFilteringByArchitectureGroup',
      'cancelTimeFlexibleAggregationRequests',
      'getRecalculatingSpecificPricingGroups',
    ]),
    ...mapHierarchyActions([
      'markUnitApprove',
      'updatePgApproval',
      'updateCategoryApproval',
      'updateHierarchy',
      'updateHierarchies',
      'fetchHierarchy',
    ]),
    ...mapActions('scenarioMetadata', [
      'getAggregatedScenarios',
      'fetchAllScenariosForWorkpackage',
    ]),
    ...mapActions('filters', ['resetState', 'setSelectedFilter']),
    ...mapMutations('gridView', ['setLoadingHierarchyView']),

    toggleExpansion({ parentId, level, beingExpanded, emit }) {
      // remove all items below the expanded level
      this.setExpandedHierarchyLevelItems({
        newExpandedHierarchy: fill([...this.expandedHierarchyLevelItems], null, level + 1),
      });
      // PG level and unitMangerView filters state resets in onExpandProductView

      if (level !== pricingGroupLevel && !this.isUnitManagerView) {
        // remove pricing group selection on expansion change
        this.resetState();
      }

      // this means the are collapsing an expanded row
      if (!beingExpanded) return;
      const requests = [];

      if (this.toggleLogic[getSingleAggregations]) {
        // trigger a dispatch for the old aggregations
        requests.push(
          this.fetchAggregatedHierarchyLevelItems({
            aggregationLevel: level + 1,
            parentId,
          })
        );
      }

      if (level === pricingGroupLevel || (level === unitLevel && this.isUnitManagerView)) {
        // in case a user has updated the hierarchies in another tab
        requests.push(this.fetchHierarchy());
      }
      // don't remove "to", it's required to prevent printing of "unhandled promise rejection" errors
      to(Promise.all(requests));

      const newExpandedHierarchy = [...this.expandedHierarchyLevelItems];
      newExpandedHierarchy[level + 1] = parentId;
      this.setExpandedHierarchyLevelItems({ newExpandedHierarchy });
      if (emit) {
        this.globalEmit('toggle-expansion', { parentId, level, beingExpanded });
      }
    },

    setGridViewFilter(attributeFilters) {
      this.setSelectedFilter({
        filterName: 'retailAttributesFilter',
        filterValue: attributeFilters,
      });
      if (this.debounceUpdateGridViewInstance) {
        this.debounceUpdateGridViewInstance();
      }
    },

    async updateGridView() {
      this.setLoadingHierarchyView(true);
      await Promise.all([
        this.fetchItems({
          levelEntryKey: this.selectedLevelEntryKey,
          isUnitManagerView: this.isUnitManagerView,
        }),
        await this.getAlertCounts(),
      ]);
      // don't remove "to", it's required to prevent printing of "unhandled promise rejection" errors
      await to(
        Promise.all([
          this.fetchAggregations(),
          ...(!this.isUnitManagerView ? [this.getAggregatedScenarios()] : []),
        ]).then(this.globalEmit('hierarchy-refreshed'))
      );
      this.setLoadingHierarchyView(false);
    },

    async releasePricingGroup(shouldRelease = false) {
      if (shouldRelease) {
        if (this.useReleaseCheckboxApprovals && !isEmpty(this.pricingGroupsToRelease)) {
          // When the useReleaseCheckboxApprovals is on, make sure the approval record
          // follows the checkbox selection before the actual release update is persisted.
          await this.applyReleaseCheckboxApprovals(this.pricingGroupsToRelease, true);
        }

        if (this.categoryToRelease) {
          // Bulk release triggered via the category release dialog.
          await this.updateHierarchies({ updates: this.formatUpdates() });
        } else if (this.pricingGroupToRelease) {
          // Single pricing group release via the confirmation dialog.
          await this.updateHierarchy({
            levelEntryKey: this.pricingGroupToRelease.levelEntryKey,
            updates: { released: { isReleased: true, releaseDate: Date.now() } },
          });
        }
      } else if (this.useReleaseCheckboxApprovals && !isEmpty(this.pricingGroupsToRelease)) {
        // Rolling back approvals when the user cancels out of the confirmation dialog.
        await this.applyReleaseCheckboxApprovals(this.pricingGroupsToRelease, false);
      }

      this.resetPricingGroupsToRelease();
      this.setCategoryToRelease({ category: null });
      this.pricingGroupToRelease = null;
      this.isReleaseConfirmDialogueOpen = false;
      this.releaseHasValidationErrors = false;
    },
    async releaseCategory(category) {
      const pricingGroups = this.getHierarchy(
        pg =>
          pg.parentId === category.levelEntryKey && get(pg, 'released.isReleased', false) === false
      );
      this.setPricingGroupsToRelease({ pricingGroups });
      this.isReleaseConfirmDialogueOpen = true;
      this.checkReleaseValidationErrors();
    },
    async releasePG(payload = {}) {
      const selected = payload.selected !== false;
      const pricingGroup = payload.pricingGroup || this.pricingGroupsToRelease[0];

      if (this.useReleaseCheckboxApprovals && pricingGroup) {
        // Early exit for uncheck: apply approval rollback and skip the confirm dialog.
        await this.applyReleaseCheckboxApprovals([pricingGroup], selected);
      }

      if (!selected) {
        return;
      }

      this.pricingGroupToRelease = pricingGroup;
      this.isReleaseConfirmDialogueOpen = true;
      this.checkReleaseValidationErrors();
    },

    async checkReleaseValidationErrors() {
      this.releaseHasValidationErrors = false;
      if (!this.isPriceValidationsEnabled || !this.selectedWorkpackage) return;

      const workpackageId = this.selectedWorkpackage._id;
      const scenarioKey = this.scenario;
      if (!scenarioKey) return;

      const [err, response] = await to(
        this.$http.get(`/api/price-validations/workpackage/${workpackageId}/has-errors`, {
          params: { scenarioKey },
        })
      );
      if (!err && response.data) {
        this.releaseHasValidationErrors = response.data.hasErrors;
      }
    },

    getUnitLevelEntryKey(pricingGroupLevelEntryKey) {
      const pricingGroup = this.getHierarchy(
        pg => pg.levelEntryKey === pricingGroupLevelEntryKey
      )[0];
      if (!pricingGroup) return null;

      const category = this.getHierarchy(cat => cat.levelEntryKey === pricingGroup.parentId)[0];
      return get(category, 'parentId', null);
    },

    async applyReleaseCheckboxApprovals(pricingGroups, approved) {
      if (!this.useReleaseCheckboxApprovals || isEmpty(pricingGroups)) return;

      const approvalUpdate = {
        approved,
        approvalDate: Date.now(),
        userId: this.profile.username,
      };

      // Track which units we've already updated to avoid duplicate UM approval calls when multiple PGs share a unit.
      const processedUnits = new Set();

      // eslint-disable-next-line no-restricted-syntax
      for (const pricingGroup of pricingGroups) {
        const unitLevelEntryKey = this.getUnitLevelEntryKey(pricingGroup.levelEntryKey);
        const hierarchyUpdates = {
          'approvalStatus.pricingSpecialistApproval': approvalUpdate,
        };

        // When unchecking, also clear the release flag so PG is no longer considered released.
        if (!approved) {
          hierarchyUpdates.released = { isReleased: false, releaseDate: null };
        }

        const approvalPromises = [
          this.updateHierarchy({
            levelEntryKey: pricingGroup.levelEntryKey,
            updates: hierarchyUpdates,
          }),
          this.updatePgApproval({
            levelEntryKey: pricingGroup.levelEntryKey,
            updates: { 'approvalStatus.categoryManagerApproval': approvalUpdate },
          }),
        ];

        if (unitLevelEntryKey && !processedUnits.has(unitLevelEntryKey)) {
          processedUnits.add(unitLevelEntryKey);
          approvalPromises.push(
            this.markUnitApprove({
              levelEntryKey: unitLevelEntryKey,
              updates: { 'approvalStatus.unitManagerApproval': approvalUpdate },
              sapExportFields: this.getSapExportFields(),
            })
          );
        }

        // eslint-disable-next-line no-await-in-loop
        await Promise.all(approvalPromises);
      }
    },

    pricingSpecialistPricingGroupApproval({ levelEntryKey, approved }) {
      this.updateHierarchy({
        levelEntryKey,
        updates: {
          'approvalStatus.pricingSpecialistApproval': {
            approved,
            approvalDate: Date.now(),
            userId: this.profile.username,
          },
        },
      });
    },

    categoryManagerPricingGroupApproval({ levelEntryKey, approved }) {
      this.updatePgApproval({
        levelEntryKey,
        updates: {
          'approvalStatus.categoryManagerApproval': {
            approved,
            approvalDate: Date.now(),
            userId: this.profile.username,
          },
        },
      });
    },

    pricingSpecialistCategoryApproval({ levelEntryKey, approved }) {
      const pricingGroups = this.getHierarchy(pg => pg.parentId === levelEntryKey);
      this.updateCategoryApproval({
        updates: this.formatApprovalUpdates(pricingGroups, approved, 'pricingSpecialistApproval'),
      });
    },

    categoryManagerCategoryApproval({ levelEntryKey, approved }) {
      const pricingGroups = this.getHierarchy(pg => pg.parentId === levelEntryKey);
      this.updateCategoryApproval({
        updates: this.formatApprovalUpdates(pricingGroups, approved, 'categoryManagerApproval'),
      });
    },

    approveUnit({ levelEntryKey }) {
      const sapExportFields = this.getSapExportFields();
      this.markUnitApprove({
        levelEntryKey,
        updates: {
          'approvalStatus.unitManagerApproval': {
            approved: true,
            approvalDate: Date.now(),
            userId: this.profile.username,
          },
        },
        sapExportFields,
      });
    },

    formatUpdates() {
      const updates = this.pricingGroupsToRelease.map(pg => ({
        filter: {
          levelEntryKey: pg.levelEntryKey,
        },
        update: {
          released: {
            isReleased: true,
            releaseDate: Date.now(),
          },
        },
      }));
      return updates;
    },
    formatApprovalUpdates(pricingGroups, approved, approver) {
      const updates = pricingGroups.map(pg => ({
        filter: {
          levelEntryKey: pg.levelEntryKey,
        },
        update: {
          [`approvalStatus.${approver}`]: {
            approved,
            approvalDate: Date.now(),
            userId: this.profile.username,
          },
        },
      }));
      return updates;
    },

    async onToggleFilteringByArchitectureGroup({
      isFilterEnabled,
      architectureGroupAttribute,
      architectureGroupId,
    }) {
      const attributeFiltersToKeep = this.retailAttributesFilter.filter(
        attr => attr.attributeKey === this.toolStoreGroupGlobalAttributeKey
      );
      await this.toggleFilteringByArchitectureGroup({
        isFilterEnabled,
        architectureGroupAttribute,
        architectureGroupId,
        attributeFiltersToKeep,
      });
      if (this.debounceUpdateGridViewInstance) {
        this.debounceUpdateGridViewInstance();
      }
    },

    async fetchFreshGridviewAggregations() {
      await this.fetchGroupedAggregatedHierarchyLevelItems({
        refreshCache: true,
        loadingMsg: 'caching.gridview.loadingMessage',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.notification-text {
  color: $pricing-grey-dark;
  font-size: $error-text-font-size;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
@import '@style/base/_variables.scss';

.attribute-filter-panel {
  position: sticky;
  top: 112px;
  z-index: 9;
}

.hierarchy {
  &__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    &.sticky-header th {
      position: sticky;
    }
  }

  &__row {
    background-color: $pricing-white;

    &--group-headers,
    &--headers {
      th {
        background: $pricing-background;
        z-index: 6;
        text-align: left;
        padding: 0 1rem;
      }
    }

    &--group-headers th {
      top: 11.1rem;
    }

    &--headers th {
      top: 15.7rem;
    }

    td {
      padding: 0.4rem 0.7rem;
      vertical-align: middle;
    }
  }
}
</style>
